import React, { useState, useEffect, useCallback } from 'react';
import { 
  ReactFlow, 
  MiniMap, 
  Controls, 
  Background, 
  useNodesState, 
  useEdgesState, 
  addEdge 
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Calendar, User, CheckCircle, Clock, AlertCircle, XCircle, Play } from "lucide-react";
import { updateApprovalStatusIPR } from "../../../../ApiCalls/mantisFlowsIPRs";

// Componente personalizado para nodos de aprobación
const ApprovalNode = ({ data }) => {
  // Log de datos que llegan al ApprovalNode
  console.log(`🎨 ApprovalNode - Datos recibidos (Paso ${data.stepNumber}):`, {
    status: data.status,
    canApprove: data.canApprove,
    assignee: data.assignee,
    email: data.email,
    shouldShowButton: data.status === "ready" && data.canApprove,
    shouldShowNoPermission: data.status === "ready" && !data.canApprove,
    allData: data,
  });

  const getStatusColor = (status, stepApproved) => {
    if (stepApproved) {
      return "border-green-400 bg-gradient-to-br from-green-50 to-green-100 text-green-800 shadow-green-100";
    }
    switch (status) {
      case "ready":
        return "border-green-400 bg-gradient-to-br from-green-50 to-green-100 text-green-800 shadow-green-100";
      case "approved":
        return "border-blue-400 bg-gradient-to-br from-blue-50 to-blue-100 text-blue-800 shadow-blue-100";
      case "pending":
        return "border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100 text-yellow-800 shadow-yellow-100";
      case "rejected":
        return "border-red-400 bg-gradient-to-br from-red-50 to-red-100 text-red-800 shadow-red-100";
      default:
        return "border-slate-300 bg-gradient-to-br from-slate-50 to-slate-100 text-slate-700 shadow-slate-100";
    }
  };

  const getStatusIcon = (status, stepApproved) => {
    if (stepApproved) {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    }
    switch (status) {
      case "ready":
        return <Play className="h-4 w-4 text-green-600" />;
      case "approved":
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-600" />;
      case "rejected":
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-slate-400" />;
    }
  };

  return (
    <div
      className={`px-4 py-4 rounded-xl border-2 min-w-[280px] shadow-lg ${getStatusColor(
        data.status,
        data.stepApproved
      )} dark:from-slate-800 dark:to-slate-900 dark:border-slate-600 dark:text-slate-200`}
    >
      {/* Header con status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          {/* Número de paso en círculo */}
          {data.stepNumber && (
            <div
              className={`w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-bold mr-2 ${
                data.stepNumber === "✓"
                  ? "bg-green-500"
                  : data.stepNumber === "🏁"
                  ? "bg-purple-500"
                  : "bg-indigo-500"
              }`}
            >
              {data.stepNumber}
            </div>
          )}
          {getStatusIcon(data.status, data.stepApproved)}
          <Badge
            variant={data.status === "ready" ? "default" : "secondary"}
            className="text-xs px-2 py-1 font-medium"
          >
            {data.status === "ready"
              ? "Listo para iniciar"
              : data.status === "approved" || data.stepApproved
              ? "Aprobado"
              : data.status === "pending"
              ? "Pendiente"
              : data.status === "rejected"
              ? "Rechazado"
              : data.status}
          </Badge>
        </div>
        {data.dueDate && (
          <div className="flex items-center space-x-1 text-xs bg-white dark:bg-slate-700 px-2 py-1 rounded-full">
            <Calendar className="h-3 w-3" />
            <span>{data.dueDate}</span>
          </div>
        )}
      </div>

      {/* Título */}
      <h3 className="font-semibold text-base mb-3 text-slate-800 dark:text-slate-200">
        {data.label}
      </h3>

      {/* Assignee */}
      {data.assignee && (
        <div className="flex items-center space-x-2 mb-3 bg-white dark:bg-slate-700 p-2 rounded">
          <User className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {data.assignee}
          </span>
        </div>
      )}

      {/* Email */}
      {data.email && data.email !== "undefined" && (
        <div className="text-xs text-slate-600 dark:text-slate-400 mb-2">
          📧 {data.email}
        </div>
      )}

      {/* Canal */}
      {data.channel && data.channel !== "undefined" && (
        <div className="text-xs text-slate-600 dark:text-slate-400 mb-3">
          📢 Canal: {data.channel}
        </div>
      )}

      {/* Action buttons - Solo mostrar si el usuario puede aprobar */}
      {data.canApprove && (
        <div className="space-y-2">
          <Button
            onClick={data.onApprove}
            disabled={
              data.isApproving ||
              data.isRejecting ||
              data.status === "rejected" ||
              data.status === "approved"
            }
            className="w-full text-sm h-9 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-medium shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {data.isApproving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Aprobando...
              </>
            ) : (
              "Aprobar"
            )}
          </Button>

          <Button
            onClick={data.onReject}
            disabled={
              data.isApproving ||
              data.isRejecting ||
              data.status === "rejected" ||
              data.status === "approved"
            }
            className="w-full text-sm h-9 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white font-medium shadow-md transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {data.isRejecting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Rechazando...
              </>
            ) : (
              "Rechazar"
            )}
          </Button>
        </div>
      )}

      {/* Mostrar mensaje si el step ya fue aprobado o rechazado */}
      {data.status === "approved" && (
        <div className="w-full text-sm h-9 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-200 rounded flex items-center justify-center border-2 border-green-300 dark:border-green-700 font-medium">
          ✅ Paso Aprobado
        </div>
      )}

      {data.status === "rejected" && (
        <div className="w-full text-sm h-9 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-200 rounded flex items-center justify-center border-2 border-red-300 dark:border-red-700 font-medium">
          ❌ Paso Rechazado
        </div>
      )}
    </div>
  );
};

// Componente personalizado para nodos de decisión
const DecisionNode = ({ data }) => {
  return (
    <div className="px-4 py-4 rounded-xl border-2 border-purple-400 bg-gradient-to-br from-purple-50 to-purple-100 text-purple-800 min-w-[250px] shadow-lg shadow-purple-100 dark:from-purple-900 dark:to-purple-800 dark:border-purple-600 dark:text-purple-200">
      <div className="text-center">
        <h3 className="font-semibold text-base mb-2 text-purple-800 dark:text-purple-200">
          {data.label}
        </h3>
        <p className="text-sm text-purple-600 dark:text-purple-300">
          {data.description}
        </p>
      </div>
    </div>
  );
};

// Tipos de nodos personalizados
const nodeTypes = {
  approval: ApprovalNode,
  decision: DecisionNode,
};

function WorkflowSection({
  data,
  clientId,
  currentUser,
  userApprovalSteps,
  currentUserId,
  currentUserEmail,
}) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [approvingNodeId, setApprovingNodeId] = useState(null);
  const [rejectingNodeId, setRejectingNodeId] = useState(null);

  // Log completo de datos de entrada para debugging
  console.log("🔍 WorkflowSection - Datos de entrada completos:", {
    currentUser,
    currentUserId,
    currentUserEmail,
    userApprovalSteps,
    dataApprovalPlan: data?.approval_plan,
    dataKeys: data ? Object.keys(data) : "no data",
    clientId,
  });

  // LÓGICA ANTERIOR COMENTADA - Usaba validationData del endpoint
  /*
  const generateWorkflowFromValidation = useCallback(() => {
    const validationData = data?.validationData;
    
    if (!validationData || !validationData.requiredApprovals) {
      // Si no hay datos de validación, usar un workflow básico
      return {
        nodes: [
          {
            id: '1',
            type: 'approval',
            position: { x: 100, y: 50 },
            data: {
              label: 'Inicio del Proceso',
              assignee: 'Sistema',
              status: 'approved',
              dueDate: 'Completado'
            }
          }
        ],
        edges: []
      };
    }

    const requiredApprovals = validationData.requiredApprovals || [];
    const triggeredRules = validationData.triggeredRules || [];
    
    const workflowNodes = [];
    const workflowEdges = [];

    // Nodo inicial
    workflowNodes.push({
      id: '0',
      type: 'approval',
      position: { x: 100, y: 50 },
      data: {
        label: 'Solicitud Creada',
        assignee: data?.created_by_name || 'Usuario',
        status: 'approved',
        dueDate: data?.created_at?.slice(0, 10) || 'Completado'
      }
    });

    // Mapear nombres de aprobadores a nombres más descriptivos
    const approvalNames = {
      'compras': 'Aprobación de Compras',
      'legal': 'Revisión Legal',
      'finanzas': 'Aprobación Financiera',
      'gerencia': 'Aprobación Gerencial',
      'it': 'Revisión IT',
      'seguridad': 'Revisión de Seguridad'
    };

    // Crear nodos para cada aprobación requerida
    requiredApprovals.forEach((approval, index) => {
      const nodeId = (index + 1).toString();
      const isFirst = index === 0;
      
      workflowNodes.push({
        id: nodeId,
        type: 'approval',
        position: { x: 100 + (index + 1) * 300, y: 50 },
        data: {
          label: approvalNames[approval] || `Aprobación ${approval}`,
          assignee: `Departamento ${approval.toUpperCase()}`,
          status: isFirst ? 'ready' : 'pending',
          dueDate: 'Por definir',
          approvalType: approval,
          onApprove: isFirst ? () => simulateApproval(nodeId) : undefined
        }
      });

      // Crear edge desde el nodo anterior
      if (index === 0) {
        workflowEdges.push({
          id: `e0-${nodeId}`,
          source: '0',
          target: nodeId,
          animated: true
        });
      } else {
        workflowEdges.push({
          id: `e${index}-${nodeId}`,
          source: index.toString(),
          target: nodeId,
          animated: true
        });
      }
    });

    // Nodo final
    if (requiredApprovals.length > 0) {
      const finalNodeId = (requiredApprovals.length + 1).toString();
      workflowNodes.push({
        id: finalNodeId,
        type: 'approval',
        position: { x: 100 + (requiredApprovals.length + 1) * 300, y: 50 },
        data: {
          label: 'Proceso Completado',
          assignee: 'Sistema',
          status: 'pending',
          dueDate: 'Pendiente'
        }
      });

      workflowEdges.push({
        id: `e${requiredApprovals.length}-${finalNodeId}`,
        source: requiredApprovals.length.toString(),
        target: finalNodeId,
        animated: true
      });
    }

    return { nodes: workflowNodes, edges: workflowEdges };
  }, [data]);
  */

  // NUEVA LÓGICA - Generar workflow dinámico basado en userApprovalSteps
  const generateWorkflowFromApprovalPlan = useCallback(() => {
    // Usar userApprovalSteps si está disponible, sino usar approval_plan
    const approvalPlan = userApprovalSteps || data?.approval_plan;

    // Log para debugging
    console.log("🔧 Generando workflow con:", {
      userApprovalSteps: userApprovalSteps,
      dataApprovalPlan: data?.approval_plan,
      finalApprovalPlan: approvalPlan,
      planLength: approvalPlan?.length,
    });

    if (
      !approvalPlan ||
      !Array.isArray(approvalPlan) ||
      approvalPlan.length === 0
    ) {
      // Si no hay approval_plan, usar un workflow básico
      return {
        nodes: [
          {
            id: "1",
            type: "approval",
            position: { x: 100, y: 50 },
            data: {
              label: "Inicio del Proceso",
              assignee: "Sistema",
              status: "approved",
              dueDate: "Completado",
            },
          },
        ],
        edges: [],
      };
    }

    const workflowNodes = [];
    const workflowEdges = [];

    // Nodo inicial
    workflowNodes.push({
      id: "0",
      type: "approval",
      position: { x: 50, y: 100 },
      data: {
        label: "Solicitud Creada",
        assignee: data?.created_by_name || "Usuario",
        status: "approved",
        dueDate: data?.created_at?.slice(0, 10) || "Completado",
        stepNumber: "✓",
      },
    });

    // Crear nodos para cada step del approval_plan
    approvalPlan.forEach((step, index) => {
      const nodeId = (index + 1).toString();
      const isFirst = index === 0;

      // Log detallado de cada step para debugging
      console.log(`🔍 WorkflowSection - Step ${index + 1} detalles:`, {
        stepIndex: index,
        stepData: step,
        responsible: step.responsible,
        email: step.email,
        firstName: step.firstName,
        lastName: step.lastName,
        canApprove: step.canApprove,
        stepApproved: step.stepApproved,
        action: step.action,
        currentUserEmail: currentUserEmail,
        currentUserId: currentUserId,
        emailMatch:
          step.responsible === currentUserEmail ||
          step.email === currentUserEmail,
      });

      // Construir nombre completo con validación de undefined/null
      const firstName =
        step.firstName && typeof step.firstName === "string"
          ? step.firstName.trim()
          : "";
      const lastName =
        step.lastName && typeof step.lastName === "string"
          ? step.lastName.trim()
          : "";

      let fullName;
      if (firstName && lastName) {
        fullName = `${firstName} ${lastName}`;
      } else if (firstName) {
        fullName = firstName;
      } else if (lastName) {
        fullName = lastName;
      } else if (step.responsible && typeof step.responsible === "string") {
        fullName = step.responsible.trim();
      } else {
        fullName = "Usuario";
      }

      // Mapear acción a etiqueta
      const actionLabels = {
        approve: "Aprobación",
        notify: "Notificación",
        review: "Revisión",
        validate: "Validación",
      };

      const actionLabel =
        actionLabels[step.action] ||
        (step.action && typeof step.action === "string"
          ? step.action
          : "Acción");

      // Determinar el estado del nodo basado en si el usuario puede aprobar
      let nodeStatus = "pending";

      if (isFirst) {
        // Si es el primer paso, verificar si el usuario puede aprobar
        nodeStatus = step.canApprove ? "ready" : "pending";

        // Log específico del primer step para debugging
        console.log(`🎯 WorkflowSection - Primer step (${index + 1}) estado:`, {
          stepIndex: index,
          canApprove: step.canApprove,
          nodeStatus: nodeStatus,
          responsible: step.responsible,
          email: step.email,
          currentUserEmail: currentUserEmail,
          willShowButton: nodeStatus === "ready" && step.canApprove,
        });
      }

      // Verificar si el step ya fue aprobado o rechazado
      if (step.stepApproved) {
        nodeStatus = "approved";
      } else if (step.stepRejected) {
        nodeStatus = "rejected";
      }

      const nodeData = {
        label: `${actionLabel} - Paso ${step.step || index + 1}`,
        assignee: fullName,
        status: nodeStatus,
        dueDate: step.deadline_hours
          ? `${step.deadline_hours}h`
          : "Por definir",
        approvalType: step.action,
        email:
          step.email && typeof step.email === "string"
            ? step.email.trim()
            : step.responsible &&
              typeof step.responsible === "string" &&
              step.responsible.includes("@")
            ? step.responsible.trim()
            : undefined,
        phone:
          step.cellPhone && typeof step.cellPhone === "string"
            ? step.cellPhone.trim()
            : undefined,
        message:
          step.message && typeof step.message === "string"
            ? step.message.trim()
            : undefined,
        channel:
          step.channel && typeof step.channel === "string"
            ? step.channel.trim()
            : undefined,
        reminderHours:
          step.reminder_hours && !isNaN(step.reminder_hours)
            ? step.reminder_hours
            : undefined,
        stepNumber: step.step || index + 1,
        canApprove: step.canApprove, // Añadir información de permisos
        stepApproved: step.stepApproved,
        stepRejected: step.stepRejected,
        stepId: nodeId, // Agregar ID del step para referencia
        stepIndex: index, // Agregar índice del step para referencia
      };

      // Log de los datos del nodo que se va a crear
      console.log(`🏗️ WorkflowSection - Creando nodo ${nodeId}:`, {
        nodeId: nodeId,
        nodeData: nodeData,
        showButton: nodeData.status === "ready" && nodeData.canApprove,
        isFirstStep: isFirst,
      });

      workflowNodes.push({
        id: nodeId,
        type: "approval",
        position: { x: 50 + (index + 1) * 350, y: 100 },
        data: nodeData,
      });

      // Crear edge desde el nodo anterior
      if (index === 0) {
        const edgeColor = step.stepRejected ? "#ef4444" : "#4f46e5";
        workflowEdges.push({
          id: `e0-${nodeId}`,
          source: "0",
          target: nodeId,
          animated: true,
          type: "smoothstep",
          style: {
            stroke: edgeColor,
            strokeWidth: 4,
            strokeDasharray: "8,4",
          },
          markerEnd: {
            type: "arrowclosed",
            color: edgeColor,
            width: 25,
            height: 25,
          },
          label: `→ Paso ${step.step || index + 1}`,
          labelStyle: {
            fontSize: "12px",
            fontWeight: "bold",
            color: edgeColor,
          },
          labelBgStyle: {
            fill: "white",
            fillOpacity: 0.9,
            stroke: edgeColor,
            strokeWidth: 1,
          },
        });
      } else {
        const edgeColor = step.stepRejected ? "#ef4444" : "#4f46e5";
        workflowEdges.push({
          id: `e${index}-${nodeId}`,
          source: index.toString(),
          target: nodeId,
          animated: true,
          type: "smoothstep",
          style: {
            stroke: edgeColor,
            strokeWidth: 4,
            strokeDasharray: "8,4",
          },
          markerEnd: {
            type: "arrowclosed",
            color: edgeColor,
            width: 25,
            height: 25,
          },
          label: `→ Paso ${step.step || index + 1}`,
          labelStyle: {
            fontSize: "12px",
            fontWeight: "bold",
            color: edgeColor,
          },
          labelBgStyle: {
            fill: "white",
            fillOpacity: 0.9,
            stroke: edgeColor,
            strokeWidth: 1,
          },
        });
      }
    });

    // Nodo final
    if (approvalPlan.length > 0) {
      const finalStep = approvalPlan[approvalPlan.length - 1];
      const finalStepApproved = finalStep.stepApproved;
      const finalStepRejected = finalStep.stepRejected;

      let finalStepStatus = "pending";
      if (finalStepApproved) {
        finalStepStatus = "approved";
      } else if (finalStepRejected) {
        finalStepStatus = "rejected";
      }

      const finalNodeId = (approvalPlan.length + 1).toString();
      workflowNodes.push({
        id: finalNodeId,
        type: "approval",
        position: { x: 50 + (approvalPlan.length + 1) * 350, y: 100 },
        data: {
          label: "Proceso Completado",
          status: finalStepStatus,
          dueDate: "Pendiente",
          stepNumber: "🏁",
        },
      });

      workflowEdges.push({
        id: `e${approvalPlan.length}-${finalNodeId}`,
        source: approvalPlan.length.toString(),
        target: finalNodeId,
        animated: true,
        type: "smoothstep",
        style: {
          stroke: "#10b981",
          strokeWidth: 4,
          strokeDasharray: "8,4",
        },
        markerEnd: {
          type: "arrowclosed",
          color: "#10b981",
          width: 25,
          height: 25,
        },
        label: "✓ Completar",
        labelStyle: {
          fontSize: "12px",
          fontWeight: "bold",
          color: "#10b981",
        },
        labelBgStyle: {
          fill: "white",
          fillOpacity: 0.9,
          stroke: "#10b981",
          strokeWidth: 1,
        },
      });
    }

    return { nodes: workflowNodes, edges: workflowEdges };
  }, [data, userApprovalSteps]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Simular progreso del workflow
  const simulateApproval = useCallback(
    (nodeId) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                status: "approved",
                stepApproved: true,
                stepRejected: false,
                canApprove: false,
              },
            };
          }
          return node;
        })
      );

      // Activar el siguiente nodo
      const nextNodeId = (parseInt(nodeId) + 1).toString();
      setTimeout(() => {
        setNodes((nds) =>
          nds.map((node) => {
            if (node.id === nextNodeId && node.data.status === "pending") {
              return {
                ...node,
                data: {
                  ...node.data,
                  status: "ready",
                },
              };
            }
            return node;
          })
        );
      }, 500);
    },
    [setNodes]
  );

  // Simular rechazo del workflow
  const simulateRejection = useCallback(
    (nodeId) => {
      setNodes((nds) =>
        nds.map((node) => {
          if (node.id === nodeId) {
            return {
              ...node,
              data: {
                ...node.data,
                status: "rejected",
                stepApproved: false,
                stepRejected: true,
                canApprove: false,
              },
            };
          }
          return node;
        })
      );
    },
    [setNodes]
  );

  // Manejar la aprobación real del workflow
  const handleApproval = useCallback(
    async (nodeId) => {
      try {
        // Establecer estado de carga
        setApprovingNodeId(nodeId);

        // Obtener información del step actual usando userApprovalSteps o approval_plan
        const stepsList = userApprovalSteps || data?.approval_plan || [];

        // El nodeId corresponde al índice + 1, por lo que restamos 1 para obtener el índice
        const stepIndex = parseInt(nodeId) - 1;
        const currentStep = stepsList[stepIndex];

        if (!currentStep) {
          console.error(
            "No se encontró el step para aprobar. NodeId:",
            nodeId,
            "StepIndex:",
            stepIndex,
            "Steps disponibles:",
            stepsList.length
          );
          return;
        }

        // Verificar permisos del usuario
        if (!currentStep.canApprove) {
          console.error("Usuario sin permisos para aprobar este step:", {
            stepNumber: currentStep.step,
            responsible: currentStep.responsible,
            canApprove: currentStep.canApprove,
          });
          return;
        }

        // Log de información de la aprobación
        console.log("🎯 Aprobación iniciada:", {
          nodeId: nodeId,
          stepIndex: stepIndex,
          stepNumber: currentStep.step || stepIndex + 1,
          currentUser: {
            id: currentUserId,
            email: currentUserEmail,
            name: currentUser,
          },
          stepDetails: {
            responsible: currentStep.responsible,
            action: currentStep.action,
            canApprove: currentStep.canApprove,
            firstName: currentStep.firstName,
            lastName: currentStep.lastName,
            email: currentStep.email,
          },
          iprId: data?.id,
        });

        // TODO: Aquí se haría la llamada al backend para aprobar el step
        const approvalResponse = await updateApprovalStatusIPR(
          clientId,
          currentUserId,
          data?.id,
          "approved"
        );
        console.log("🎯 Aprobación completada:", approvalResponse);

        if (approvalResponse.success) {
          console.log("🎯 Aprobación completada:", approvalResponse);
          // Simular progreso del workflow
          simulateApproval(nodeId);
        } else {
          console.error("🚨 Error al aprobar el step:", approvalResponse);
        }
      } catch (error) {
        console.error("🚨 Error durante la aprobación:", error);
      } finally {
        // Limpiar estado de carga
        setApprovingNodeId(null);
      }
    },
    [
      userApprovalSteps,
      data?.approval_plan,
      data?.id,
      currentUserId,
      currentUserEmail,
      currentUser,
      simulateApproval,
    ]
  );

  // Manejar el rechazo real del workflow
  const handleRejection = useCallback(
    async (nodeId) => {
      try {
        // Establecer estado de carga
        setRejectingNodeId(nodeId);

        // Obtener información del step actual usando userApprovalSteps o approval_plan
        const stepsList = userApprovalSteps || data?.approval_plan || [];

        // El nodeId corresponde al índice + 1, por lo que restamos 1 para obtener el índice
        const stepIndex = parseInt(nodeId) - 1;
        const currentStep = stepsList[stepIndex];

        if (!currentStep) {
          console.error(
            "No se encontró el step para rechazar. NodeId:",
            nodeId,
            "StepIndex:",
            stepIndex,
            "Steps disponibles:",
            stepsList.length
          );
          return;
        }

        // Verificar permisos del usuario
        if (!currentStep.canApprove) {
          console.error("Usuario sin permisos para rechazar este step:", {
            stepNumber: currentStep.step,
            responsible: currentStep.responsible,
            canApprove: currentStep.canApprove,
          });
          return;
        }

        // Log de información del rechazo
        console.log("🚫 Rechazo iniciado:", {
          nodeId: nodeId,
          stepIndex: stepIndex,
          stepNumber: currentStep.step || stepIndex + 1,
          currentUser: {
            id: currentUserId,
            email: currentUserEmail,
            name: currentUser,
          },
          stepDetails: {
            responsible: currentStep.responsible,
            action: currentStep.action,
            canApprove: currentStep.canApprove,
            firstName: currentStep.firstName,
            lastName: currentStep.lastName,
            email: currentStep.email,
          },
          iprId: data?.id,
        });

        // Llamada al backend para rechazar el step
        const rejectionResponse = await updateApprovalStatusIPR(
          clientId,
          currentUserId,
          data?.id,
          "rejected"
        );
        console.log("🚫 Rechazo completado:", rejectionResponse);

        if (rejectionResponse.success) {
          console.log("🚫 Rechazo completado:", rejectionResponse);
          // Simular rechazo del workflow
          simulateRejection(nodeId);
        } else {
          console.error("🚨 Error al rechazar el step:", rejectionResponse);
        }
      } catch (error) {
        console.error("🚨 Error durante el rechazo:", error);
      } finally {
        // Limpiar estado de carga
        setRejectingNodeId(null);
      }
    },
    [
      userApprovalSteps,
      data?.approval_plan,
      data?.id,
      currentUserId,
      currentUserEmail,
      currentUser,
      simulateRejection,
    ]
  );

  const onNodeClick = useCallback(
    (event, node) => {
      setSelectedNode(node);

      // Log información detallada del nodo seleccionado
      console.log("🎯 Nodo seleccionado:", {
        nodeId: node.id,
        stepNumber: node.data.stepNumber,
        assignee: node.data.assignee,
        status: node.data.status,
        canApprove: node.data.canApprove,
        approvalType: node.data.approvalType,
        email: node.data.email,
        currentUserCanApprove: node.data.canApprove,
        currentUserEmail: currentUserEmail,
      });
    },
    [currentUserEmail]
  );

  // Inicializar el workflow cuando cambien los datos
  useEffect(() => {
    const workflowData = generateWorkflowFromApprovalPlan();
    setNodes(workflowData.nodes);
    setEdges(workflowData.edges);
  }, [generateWorkflowFromApprovalPlan, setNodes, setEdges]);

  // Actualizar los nodos con las funciones onApprove y onReject después de la inicialización
  useEffect(() => {
    console.log(
      "🔄 WorkflowSection - Actualizando nodos con funciones onApprove y onReject..."
    );

    setNodes((currentNodes) => {
      const updatedNodes = currentNodes.map((node) => {
        const shouldAddOnApprove = node.data.canApprove && !node.data.onApprove;
        const shouldAddOnReject = node.data.canApprove && !node.data.onReject;

        console.log(`🔄 Nodo ${node.id} - Verificando funciones:`, {
          nodeId: node.id,
          status: node.data.status,
          canApprove: node.data.canApprove,
          hasOnApprove: !!node.data.onApprove,
          hasOnReject: !!node.data.onReject,
          shouldAddOnApprove: shouldAddOnApprove,
          shouldAddOnReject: shouldAddOnReject,
        });

        if (shouldAddOnApprove || shouldAddOnReject) {
          console.log(`✅ Añadiendo funciones al nodo ${node.id}`);
          return {
            ...node,
            data: {
              ...node.data,
              onApprove: shouldAddOnApprove
                ? () => handleApproval(node.id)
                : node.data.onApprove,
              onReject: shouldAddOnReject
                ? () => handleRejection(node.id)
                : node.data.onReject,
              isApproving: approvingNodeId === node.id,
              isRejecting: rejectingNodeId === node.id,
            },
          };
        }
        // Actualizar el estado de carga para todos los nodos
        return {
          ...node,
          data: {
            ...node.data,
            isApproving: approvingNodeId === node.id,
            isRejecting: rejectingNodeId === node.id,
          },
        };
      });

      console.log(
        "🔄 WorkflowSection - Nodos actualizados:",
        updatedNodes.map((n) => ({
          id: n.id,
          status: n.data.status,
          canApprove: n.data.canApprove,
          hasOnApprove: !!n.data.onApprove,
          hasOnReject: !!n.data.onReject,
        }))
      );

      return updatedNodes;
    });
  }, [handleApproval, handleRejection, approvingNodeId, rejectingNodeId]);

  // Obtener información del approval_plan con permisos del usuario
  const approvalPlan = userApprovalSteps || data?.approval_plan || [];

  // Log información del usuario actual para debugging
  useEffect(() => {
    if (approvalPlan.length > 0) {
      console.log("🔍 WorkflowSection - Información del usuario actual:", {
        currentUserId,
        currentUserEmail,
        currentUser,
        approvalPlan: approvalPlan.map((step) => ({
          step: step.step,
          responsible: step.responsible,
          canApprove: step.canApprove,
          action: step.action,
        })),
      });
    }
  }, [approvalPlan, currentUserId, currentUserEmail, currentUser]);

  return (
    <div className="w-full">
      {/* React Flow Container */}
      <div
        className="relative bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-800 dark:via-slate-850 dark:to-slate-900 rounded border border-slate-200 dark:border-slate-700 shadow-lg overflow-hidden"
        style={{ height: "500px" }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.3 }}
          defaultEdgeOptions={{
            style: {
              stroke: "#4f46e5",
              strokeWidth: 4,
              strokeDasharray: "5,5",
            },
            animated: true,
            type: "smoothstep",
            markerEnd: {
              type: "arrowclosed",
              color: "#4f46e5",
              width: 20,
              height: 20,
            },
          }}
        >
          <Controls className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm" />
          <MiniMap
            nodeColor={(node) => {
              switch (node.data?.status) {
                case "ready":
                  return "#10b981";
                case "approved":
                  return "#3b82f6";
                case "pending":
                  return "#f59e0b";
                case "rejected":
                  return "#ef4444";
                default:
                  return "#6b7280";
              }
            }}
            className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm"
          />
          <Background
            variant="dots"
            gap={25}
            size={1.5}
            color="#e2e8f0"
            className="dark:opacity-20 opacity-40"
          />
        </ReactFlow>

        {/* Indicador de permisos del usuario */}
        {approvalPlan.length > 0 && (
          <div className="absolute top-4 right-4 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-3 py-2 rounded text-sm font-medium shadow-sm">
            🔑 Puedes aprobar:{" "}
            {approvalPlan.filter((step) => step.canApprove).length} de{" "}
            {approvalPlan.length} pasos
            {approvalPlan.some((step) => step.stepRejected) && (
              <div className="mt-1 text-xs text-red-700 dark:text-red-300">
                ❌ {approvalPlan.filter((step) => step.stepRejected).length}{" "}
                paso(s) rechazado(s)
              </div>
            )}
          </div>
        )}
      </div>

      {/* Panel de información del nodo seleccionado */}
      {selectedNode && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-4 right-4 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 p-5 max-w-sm backdrop-blur-sm"
        >
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-bold text-slate-900 dark:text-slate-100">
              Información del Paso
            </h4>
            <Button
              onClick={() => setSelectedNode(null)}
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0"
            >
              ✕
            </Button>
          </div>

          <div className="mb-3 p-3 bg-slate-50 dark:bg-slate-700 rounded">
            <h5 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
              {selectedNode.data.label}
            </h5>
            {selectedNode.data.assignee && (
              <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
                <User className="h-4 w-4" />
                {selectedNode.data.assignee}
              </p>
            )}
          </div>

          <div className="space-y-2">
            {selectedNode.data.email &&
              selectedNode.data.email !== "undefined" && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <span className="font-medium">📧 Email:</span>
                  <span>{selectedNode.data.email}</span>
                </div>
              )}
            {selectedNode.data.phone &&
              selectedNode.data.phone !== "undefined" && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <span className="font-medium">📱 Teléfono:</span>
                  <span>{selectedNode.data.phone}</span>
                </div>
              )}
            {selectedNode.data.channel &&
              selectedNode.data.channel !== "undefined" && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <span className="font-medium">📢 Canal:</span>
                  <span>{selectedNode.data.channel}</span>
                </div>
              )}
            {selectedNode.data.dueDate && (
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium">⏰ Plazo:</span>
                <span>{selectedNode.data.dueDate}</span>
              </div>
            )}
            {selectedNode.data.reminderHours &&
              selectedNode.data.reminderHours !== "undefined" &&
              !isNaN(selectedNode.data.reminderHours) && (
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <span className="font-medium">🔔 Recordatorio:</span>
                  <span>{selectedNode.data.reminderHours}h</span>
                </div>
              )}
            {selectedNode.data.approvalType && (
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium">🎯 Acción:</span>
                <Badge variant="secondary" className="text-xs">
                  {selectedNode.data.approvalType}
                </Badge>
              </div>
            )}
            {selectedNode.data.canApprove !== undefined && (
              <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                <span className="font-medium">🔑 Permisos:</span>
                <Badge
                  variant={
                    selectedNode.data.canApprove ? "default" : "secondary"
                  }
                  className={`text-xs ${
                    selectedNode.data.canApprove
                      ? "bg-green-100 text-green-700 border-green-300"
                      : "bg-gray-100 text-gray-500 border-gray-300"
                  }`}
                >
                  {selectedNode.data.canApprove
                    ? "Puedes aprobar"
                    : "Sin permisos"}
                </Badge>
              </div>
            )}
          </div>

          {selectedNode.data.message &&
            selectedNode.data.message !== "undefined" && (
              <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                <span className="font-medium text-blue-800 dark:text-blue-200 text-sm">
                  💬 Mensaje:
                </span>
                <p className="mt-1 text-xs text-blue-700 dark:text-blue-300">
                  {selectedNode.data.message}
                </p>
              </div>
            )}

          <Button
            onClick={() => setSelectedNode(null)}
            size="sm"
            variant="outline"
            className="w-full mt-4"
          >
            Cerrar
          </Button>
        </motion.div>
      )}
    </div>
  );
}

export default WorkflowSection;
